import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  open: boolean;
  onClose: () => void;
}

const EXAMPLE = `## Cómo llegar
Vuelo a Recife (REC), transfer ~1h. Sin vuelos directos desde Uruguay — siempre escala en GRU o GIG.

## Alojamiento
- **Parejas / luna de miel:** Tabajuba (bungalows íntimos), Nannai Resort (all-inclusive de lujo)
- **Familias:** Summerville (amplio, pool, actividades para chicos)
- **Amigos / presupuesto:** Pousada Beira Mar (ambiente social, buen precio)

## Lo que lo hace único
Las piscinas naturales de arrecife, visitadas en jangada. La marea define el horario — coordinar el check-in.

## Mejor época
Abril–agosto (seco). Diciembre–marzo: temporada alta, más lluvioso.`;

export function KnowledgeHelpModal({ open, onClose }: Props) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Cómo escribir una entrada de conocimiento
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          El contenido usa Markdown. Podés usar títulos, listas y negritas para organizar la información.
          El copiloto IA va a leer este texto cuando el destino o tema sea relevante en una conversación.
        </Typography>

        <Box sx={{ display: 'grid', gap: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="body2" fontFamily="monospace" sx={{ background: '#f5f5f5', px: 0.5, borderRadius: 0.5 }}># Título grande</Typography>
            <Typography variant="body2" color="text.secondary">→ Sección principal</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="body2" fontFamily="monospace" sx={{ background: '#f5f5f5', px: 0.5, borderRadius: 0.5 }}>## Subtítulo</Typography>
            <Typography variant="body2" color="text.secondary">→ Subsección</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="body2" fontFamily="monospace" sx={{ background: '#f5f5f5', px: 0.5, borderRadius: 0.5 }}>- ítem de lista</Typography>
            <Typography variant="body2" color="text.secondary">→ Lista con bullet</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="body2" fontFamily="monospace" sx={{ background: '#f5f5f5', px: 0.5, borderRadius: 0.5 }}>**texto en negrita**</Typography>
            <Typography variant="body2" color="text.secondary">→ Resaltado</Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" mb={1}>Ejemplo — Destino: Porto de Galinhas</Typography>
        <Box
          component="pre"
          sx={{
            background: '#f8f9fa',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            fontSize: '0.8rem',
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            m: 0,
          }}
        >
          {EXAMPLE}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" mb={1}>Tips</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[
            'Una entrada por destino o tema — no micro-entradas por hotel.',
            'Los tags deben coincidir con cómo el cliente nombra el destino (ej: "porto-de-galinhas", "brasil").',
            'Escribí como si le explicaras a un colega nuevo — claro y directo.',
            'El copiloto solo usa esta info cuando el destino/tema aparece en la conversación.',
          ].map((tip, i) => (
            <Typography key={i} variant="body2" color="text.secondary">
              · {tip}
            </Typography>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
